import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  url
}) => {
  const { language } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    // SEO translations for each language
    const seoData = {
      en: {
        siteName: "Cabanna's Party - Magical Kids Sleepover Parties in Orlando",
        defaultTitle: "Cabanna's Party - Magical Kids Sleepover Parties | Orlando, FL",
        defaultDescription: "Create unforgettable memories with themed sleepover tent parties in Orlando. Perfect for ages 6-15. Complete setup service, custom decorations, and amazing extras included. Free delivery within 25 miles.",
        defaultKeywords: "kids sleepover parties, Orlando party rentals, themed tent parties, children birthday parties, sleepover tents, party decorations Orlando, kids party planning, Central Florida parties, magical sleepover experience, princess parties, unicorn parties, superhero parties",
        pages: {
          '/': {
            title: "Magical Kids Sleepover Parties | Cabanna's Party Orlando",
            description: "Transform your child's special day with our magical themed sleepover tent parties in Orlando. Complete setup service, custom decorations, and unforgettable experiences for ages 6-15.",
            keywords: "kids sleepover parties Orlando, themed tent parties, children birthday parties, magical sleepover experience, party rentals Orlando"
          },
          '/gallery': {
            title: "Party Gallery - Amazing Themed Setups | Cabanna's Party",
            description: "Browse our stunning gallery of themed sleepover party setups in Orlando. From princess parties to superhero adventures, see how we create magical memories for kids.",
            keywords: "party gallery Orlando, themed party photos, sleepover tent setups, princess parties, unicorn parties, superhero parties, party inspiration"
          },
          '/contact': {
            title: "Contact Us - Book Your Party | Cabanna's Party Orlando",
            description: "Ready to plan the perfect sleepover party? Contact our party planning experts in Orlando. Free delivery within 25 miles. Available 7 days a week.",
            keywords: "contact party planner Orlando, book sleepover party, party planning Orlando, kids party booking, Central Florida party rentals"
          }
        }
      },
      pt: {
        siteName: "Cabanna's Party - Festas do Pijama Mágicas para Crianças em Orlando",
        defaultTitle: "Cabanna's Party - Festas do Pijama Mágicas | Orlando, FL",
        defaultDescription: "Crie memórias inesquecíveis com festas temáticas do pijama em tendas em Orlando. Perfeitas para idades de 6 a 15 anos. Serviço completo de montagem, decorações personalizadas e extras incríveis incluídos. Entrega gratuita em um raio de 40 km.",
        defaultKeywords: "festas do pijama infantis, aluguel de festas Orlando, festas temáticas em tendas, aniversários infantis, tendas para festa do pijama, decorações de festa Orlando, planejamento de festas infantis, festas Flórida Central, experiência mágica festa do pijama, festas de princesa, festas de unicórnio, festas de super-heróis",
        pages: {
          '/': {
            title: "Festas do Pijama Mágicas para Crianças | Cabanna's Party Orlando",
            description: "Transforme o dia especial do seu filho com nossas festas temáticas mágicas do pijama em tendas em Orlando. Serviço completo de montagem, decorações personalizadas e experiências inesquecíveis para idades de 6 a 15 anos.",
            keywords: "festas do pijama infantis Orlando, festas temáticas em tendas, aniversários infantis, experiência mágica festa do pijama, aluguel de festas Orlando"
          },
          '/gallery': {
            title: "Galeria de Festas - Montagens Temáticas Incríveis | Cabanna's Party",
            description: "Navegue por nossa galeria impressionante de montagens de festas do pijama temáticas em Orlando. De festas de princesa a aventuras de super-heróis, veja como criamos memórias mágicas para crianças.",
            keywords: "galeria de festas Orlando, fotos de festas temáticas, montagens de tendas para festa do pijama, festas de princesa, festas de unicórnio, festas de super-heróis, inspiração para festas"
          },
          '/contact': {
            title: "Contato - Reserve Sua Festa | Cabanna's Party Orlando",
            description: "Pronto para planejar a festa do pijama perfeita? Entre em contato com nossos especialistas em planejamento de festas em Orlando. Entrega gratuita em um raio de 40 km. Disponível 7 dias por semana.",
            keywords: "contato planejador de festas Orlando, reservar festa do pijama, planejamento de festas Orlando, reserva de festas infantis, aluguel de festas Flórida Central"
          }
        }
      },
      es: {
        siteName: "Cabanna's Party - Fiestas de Pijamas Mágicas para Niños en Orlando",
        defaultTitle: "Cabanna's Party - Fiestas de Pijamas Mágicas | Orlando, FL",
        defaultDescription: "Crea recuerdos inolvidables con fiestas temáticas de pijamas en carpas en Orlando. Perfectas para edades de 6 a 15 años. Servicio completo de montaje, decoraciones personalizadas y extras increíbles incluidos. Entrega gratuita dentro de 25 millas.",
        defaultKeywords: "fiestas de pijamas infantiles, alquiler de fiestas Orlando, fiestas temáticas en carpas, cumpleaños infantiles, carpas para fiesta de pijamas, decoraciones de fiesta Orlando, planificación de fiestas infantiles, fiestas Florida Central, experiencia mágica fiesta de pijamas, fiestas de princesas, fiestas de unicornio, fiestas de superhéroes",
        pages: {
          '/': {
            title: "Fiestas de Pijamas Mágicas para Niños | Cabanna's Party Orlando",
            description: "Transforma el día especial de tu hijo con nuestras fiestas temáticas mágicas de pijamas en carpas en Orlando. Servicio completo de montaje, decoraciones personalizadas y experiencias inolvidables para edades de 6 a 15 años.",
            keywords: "fiestas de pijamas infantiles Orlando, fiestas temáticas en carpas, cumpleaños infantiles, experiencia mágica fiesta de pijamas, alquiler de fiestas Orlando"
          },
          '/gallery': {
            title: "Galería de Fiestas - Montajes Temáticos Increíbles | Cabanna's Party",
            description: "Explora nuestra impresionante galería de montajes de fiestas de pijamas temáticas en Orlando. Desde fiestas de princesas hasta aventuras de superhéroes, ve cómo creamos recuerdos mágicos para niños.",
            keywords: "galería de fiestas Orlando, fotos de fiestas temáticas, montajes de carpas para fiesta de pijamas, fiestas de princesas, fiestas de unicornio, fiestas de superhéroes, inspiración para fiestas"
          },
          '/contact': {
            title: "Contacto - Reserva Tu Fiesta | Cabanna's Party Orlando",
            description: "¿Listo para planear la fiesta de pijamas perfecta? Contacta a nuestros expertos en planificación de fiestas en Orlando. Entrega gratuita dentro de 25 millas. Disponible 7 días a la semana.",
            keywords: "contacto planificador de fiestas Orlando, reservar fiesta de pijamas, planificación de fiestas Orlando, reserva de fiestas infantiles, alquiler de fiestas Florida Central"
          }
        }
      }
    };

    const currentSEO = seoData[language];
    const pageSEO = currentSEO.pages[location.pathname as keyof typeof currentSEO.pages];

    // Set document title
    const pageTitle = title || pageSEO?.title || currentSEO.defaultTitle;
    document.title = pageTitle;

    // Set or update meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;

      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    const pageDescription = description || pageSEO?.description || currentSEO.defaultDescription;
    const pageKeywords = keywords || pageSEO?.keywords || currentSEO.defaultKeywords;
    const baseUrl = 'https://cabannasparty.us';
    const pageUrl = url || `${baseUrl}${location.pathname}`;
    const pageImage = image || `${baseUrl}/logo512.png`;

    updateMetaTag('description', pageDescription);
    updateMetaTag('keywords', pageKeywords);
    updateMetaTag('author', "Cabanna's Party Rentals");
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('language', language === 'en' ? 'English' : language === 'pt' ? 'Portuguese' : 'Spanish');

    // Open Graph meta tags
    updateMetaTag('og:title', pageTitle, true);
    updateMetaTag('og:description', pageDescription, true);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:url', pageUrl, true);
    updateMetaTag('og:image', pageImage, true);
    updateMetaTag('og:image:alt', pageTitle, true);
    updateMetaTag('og:site_name', currentSEO.siteName, true);
    updateMetaTag('og:locale', language === 'en' ? 'en_US' : language === 'pt' ? 'pt_BR' : 'es_ES', true);

    // Twitter Card meta tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', pageTitle);
    updateMetaTag('twitter:description', pageDescription);
    updateMetaTag('twitter:image', pageImage);
    updateMetaTag('twitter:image:alt', pageTitle);

    // Additional SEO meta tags
    updateMetaTag('theme-color', '#FF6B9D');
    updateMetaTag('msapplication-TileColor', '#FF6B9D');
    updateMetaTag('apple-mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'default');
    updateMetaTag('apple-mobile-web-app-title', currentSEO.siteName);

    // Geo meta tags for local SEO
    updateMetaTag('geo.region', 'US-FL');
    updateMetaTag('geo.placename', 'Orlando, Florida');
    updateMetaTag('geo.position', '28.5383;-81.3792');
    updateMetaTag('ICBM', '28.5383, -81.3792');

    // Business meta tags
    updateMetaTag('business:contact_data:locality', 'Orlando');
    updateMetaTag('business:contact_data:region', 'Florida');
    updateMetaTag('business:contact_data:country_name', 'United States');

    // Update HTML lang attribute
    document.documentElement.lang = language === 'en' ? 'en-US' : language === 'pt' ? 'pt-BR' : 'es-ES';

    // Add structured data (JSON-LD)
    const addStructuredData = () => {
      // Remove existing structured data
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }

      const structuredData = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": currentSEO.siteName,
        "description": pageDescription,
        "url": pageUrl,
        "image": pageImage,
        "telephone": "+1-407-XXX-XXXX",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Orlando",
          "addressRegion": "FL",
          "addressCountry": "US"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "28.5383",
          "longitude": "-81.3792"
        },
        "openingHours": "Mo-Su 09:00-20:00",
        "priceRange": "$$",
        "serviceArea": {
          "@type": "GeoCircle",
          "geoMidpoint": {
            "@type": "GeoCoordinates",
            "latitude": "28.5383",
            "longitude": "-81.3792"
          },
          "geoRadius": "25 miles"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Party Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Kids Sleepover Party Rentals",
                "description": "Themed sleepover tent parties with complete setup service"
              }
            }
          ]
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "5.0",
          "reviewCount": "50"
        }
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    };

    addStructuredData();

  }, [language, location.pathname, title, description, keywords, image, url]);

  return null; // This component doesn't render anything
};

export default SEO;
